import { enableBranch, enableO2O } from '@castlery/config';
import {
  addItemQuantity,
  addToOrder,
  adjustLineItemPrice,
  // applyCoupon,
  createAWebOrderByUserId,
  createPosOrder,
  createWebOrder,
  getWebOrderByCustomer,
  getWebOrderByOrderNumber,
  getWebOrderByUid,
  mergeOrder,
  orderItemsTransfer,
  overWriteServiceProductPrice,
  reduceItemQuantity,
  refreshPrice,
  // removeCoupon,
  removeLineItem,
  selectCartItems,
  selectCurrentOrderNumber,
  selectOrderLineItems,
  setOrder,
  type AdjustPriceApiPayload,
  type ATCApiPayload,
  type LineItem,
} from '@castlery/modules-order-domain';
import { selectedRetailId } from '@castlery/modules-retails-domain';
import { trackPushToOnline, trackRetrieveOnlineCart } from '@castlery/modules-tracking-services';
import { getCurrentAdmin, logout, selectedCustomerId } from '@castlery/modules-user-domain';
import { ExtraArgument } from '@castlery/shared-redux-extra';
import type { RootState as OriginalRootState } from '@castlery/shared-redux-store';
import { createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { autoCouponMarked } from './coupon.helper';
import { logger } from '@castlery/observability';

interface RootState extends OriginalRootState {
  currentProduct?: {
    qty_increments?: number;
  };
}
// eslint-disable-next-line @nx/enforce-module-boundaries

/**
 * change quantity of the item
 * @param type
 * @param itemId
 */
export const changeQuantityCommand = createAsyncThunk(
  'order/changeQuantityCommand',
  async ({ type, itemId }: { type: 'add' | 'reduce'; itemId: number }, { dispatch, getState, rejectWithValue }) => {
    const rootState = getState() as RootState;
    const orderNumber = selectCurrentOrderNumber(rootState);
    const item = rootState.order?.order?.line_items?.find((item: LineItem) => item.id === itemId);
    if (!orderNumber) {
      return rejectWithValue('[changeQuantityCommand]:order not found');
    }
    if (!item) {
      return rejectWithValue('[changeQuantityCommand]:item not found');
    }
    const warrantyOfferId = item.warranty_items?.warranty_offer_id;
    const payload = {
      orderNumber,
      lineItemId: itemId,
      quantity:
        type === 'add'
          ? item.quantity + item.variant?.qty_increments * 1
          : item.quantity - item.variant?.qty_increments * 1,
      options: warrantyOfferId
        ? {
            warranty_offer_id: warrantyOfferId,
          }
        : {},
    };

    const params = {
      orderNumber: payload.orderNumber,
      lineItemId: payload.lineItemId,
      quantity: payload.quantity,
      options: {
        warranty_offer_id: payload.options.warranty_offer_id,
      },
    };

    try {
      const action = type === 'add' ? addItemQuantity.initiate : reduceItemQuantity.initiate;
      return dispatch(action(params)).unwrap();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * remove the item from the cart
 */
export const removeItemCommand = createAsyncThunk(
  'order/removeItemCommand',
  async (itemId: number, { dispatch, getState, rejectWithValue }) => {
    const rootState = getState() as RootState;

    const orderNumber = selectCurrentOrderNumber(rootState);
    if (!orderNumber) {
      return rejectWithValue('[removeItemCommand]:order not found');
    }
    const res = await dispatch(removeLineItem.initiate({ orderNumber, lineItemId: itemId }));
    if ('error' in res) {
      return rejectWithValue(res.error);
    }
    return res.data;
  }
);

/**
 * manually adjust the price of item ,such as set discount
 */
export const adjustPriceCommand = createAsyncThunk(
  'order/adjustPriceCommand',
  async (payload: Omit<AdjustPriceApiPayload, 'orderNumber'>, { dispatch, getState, rejectWithValue }) => {
    const rootState = getState() as RootState;

    const orderNumber = selectCurrentOrderNumber(rootState);
    if (!orderNumber) {
      return rejectWithValue('[adjustPriceCommand]:order not found');
    }
    const res = await dispatch(adjustLineItemPrice.initiate({ ...payload, orderNumber }));
    if ('error' in res) {
      return rejectWithValue(res.error);
    }
    return res.data;
  }
);
/**
 * transfer item between online-cart with pos-cart
 */
export const transferItemsCommand = createAsyncThunk(
  'order/transferItemsCommand',
  async ({ to, itemIds }: { to: 'web' | 'pos'; itemIds?: number[] }, { dispatch, getState, rejectWithValue }) => {
    const rootState = getState() as RootState;

    const orderNumber = selectCurrentOrderNumber(rootState);
    const webOrderNumber = rootState.order?.onlineOrder?.number;
    const toWeb = to === 'web';
    const toPos = to === 'pos';

    let result: number[] = [];
    if (toWeb) {
      const order = rootState.order?.order;
      result = order?.line_items?.map((item: LineItem) => item.id) || [];
    }
    if (toPos) {
      result = itemIds || [];
    }
    if (!result.length) {
      return rejectWithValue('[transferItemsCommand]:item not found');
    }

    let finalOrderNumber = toWeb ? webOrderNumber! : orderNumber!;
    if (!finalOrderNumber && toPos) {
      return rejectWithValue('Pos order not found');
    }
    if (!finalOrderNumber && toWeb) {
      const userId = selectedCustomerId(rootState) as number;
      const orderRes = await dispatch(createAWebOrderByUserId(userId));

      if ('error' in orderRes) {
        return rejectWithValue(orderRes.error);
      }
      if (!orderRes?.payload?.number) {
        return rejectWithValue('Web order not found');
      }
      finalOrderNumber = orderRes.payload.number;
    }
    if (toWeb) {
      await dispatch(trackPushToOnline(result));
    }
    if (toPos) {
      await dispatch(trackRetrieveOnlineCart(result));
    }
    const res = await dispatch(orderItemsTransfer.initiate({ number: finalOrderNumber, itemIds: result }));
    if ('error' in res) {
      return rejectWithValue(res.error);
    }
    return res.data;
  }
);

/**
 * apply coupon to order
 * use shared-services/promotion.helper.ts instead
 */
// export const applyCouponCommand = createAsyncThunk(
//   'order/applyCouponCommand',
//   async (payload: Omit<ApplyCouponApiPayload, 'number'>, { dispatch, getState, rejectWithValue }) => {
//     const rootState = getState() as RootState;
//     const orderNumber = selectCurrentOrderNumber(rootState);
//     if (!orderNumber) {
//       return rejectWithValue('[applyCouponCommand]:order not found');
//     }
//     let res: any;
//     if (ecPosFeatures.enabledNewPromotion ) {
//       res = await dispatch(applyCouponV2.initiate({ ...payload, orderNumber }));
//     } else {
//       res = await dispatch(applyCouponV1.initiate({ ...payload, number: orderNumber }));
//     }
//     if ('error' in res) {
//       return rejectWithValue(res.error);
//     }
//     return res.data;
//   }
// );

/**
 * add service into order
 */
export const addServiceCommand = createAsyncThunk(
  'order/addServiceCommand',
  async (payload: Omit<ATCApiPayload, 'number'>, { dispatch, getState, rejectWithValue }) => {
    const rootState = getState() as RootState;
    const orderNumber = selectCurrentOrderNumber(rootState);
    if (!orderNumber) {
      return rejectWithValue('[addServiceCommand]:order not found');
    }
    const res = await dispatch(addToOrder.initiate({ ...payload, number: orderNumber }));
    if ('error' in res) {
      return rejectWithValue(res.error);
    }
    return res.data;
  }
);

export const createPosOrderForCurrentAdmin = createAsyncThunk(
  'order/createPosOrderForCurrentAdmin',
  async (
    {
      errorAutoLogout = true,
    }: {
      errorAutoLogout?: boolean;
    },
    { dispatch, getState, rejectWithValue, extra }
  ) => {
    const rootState = getState() as RootState;
    const { persistenceHandles } = extra as ExtraArgument;

    const retailId = selectedRetailId(rootState) || Number(persistenceHandles.retailId.getItem());
    const { data } = await dispatch(getCurrentAdmin.initiate());
    const userId = data?.id;
    if ((!retailId && enableBranch) || !userId) {
      if (errorAutoLogout) {
        await dispatch(logout({}));
        return rejectWithValue('logout');
      }
      return rejectWithValue(
        `[createPosOrderForCurrentAdmin]:retailId or userId not found!retailId:${retailId},userId:${userId}`
      );
    }
    const res = await dispatch(createPosOrder.initiate({ retail_id: retailId, sales_user_id: userId }));
    if ('error' in res) {
      return rejectWithValue(res.error);
    }
    return res.data;
  }
);

// TODO 处理 loading
export const selectPushToOnlineBtnStatus = createSelector(
  [selectOrderLineItems, selectedCustomerId],
  (cartItems, customerId) => {
    const res = {
      disabled: true,
      loading: false,
    };
    if (cartItems && customerId) {
      res.disabled = !(cartItems.length > 0);
    }
    return res;
  }
);

// TODO 处理 loading
export const selectCheckoutBtnStatus = createSelector(
  [selectCartItems, selectedCustomerId],
  (cartItems, customerId) => {
    const res = {
      disabled: true,
      loading: false,
    };
    if (cartItems && customerId) {
      res.disabled = !(cartItems.length > 0);
    }
    return res;
  }
);

export const createANewPosOrder = createAsyncThunk('order/createANewPosOrder', async (_, { dispatch, extra }) => {
  const { persistenceHandles } = extra as ExtraArgument;
  persistenceHandles.orderNumber.removeItem();
  persistenceHandles.customerId.removeItem();
  persistenceHandles.city.removeItem();
  return await dispatch(createPosOrderForCurrentAdmin({})); // Fix: Pass an empty object as an argument
});

export const createWebOrderForCurrentUser = createAsyncThunk(
  'order/createWebOrderForCurrentUser',
  async (_, { dispatch, rejectWithValue }) => {
    const res = await dispatch(createWebOrder.initiate(undefined));
    if ('error' in res) {
      return rejectWithValue(res.error);
    }
    return res.data;
  }
);

/**
 * remove coupon from order
 * use shared-services/promotion.helper.ts instead
 */
// export const removeCouponCommand = createAsyncThunk(
//   'order/removeCouponCommand',
//   async (couponCode: string, { dispatch, getState, rejectWithValue, extra }) => {
//     const rootState = getState() as RootState;
//     const orderNumber = selectCurrentOrderNumber(rootState);
//     if (!orderNumber) {
//       return rejectWithValue('[removeCouponCommand]:order not found');
//     }
//     let res: any;
//     if (ecPosFeatures.enabledNewPromotion ) {
//       res = await dispatch(removeCouponV2.initiate({ orderNumber: orderNumber, couponCode: couponCode }));
//     } else {
//       res = await dispatch(removeCouponV1.initiate({ number: orderNumber, couponCode: couponCode }));
//     }
//     if ('error' in res) {
//       return rejectWithValue(res.error);
//     }
//     const { persistenceHandles } = extra as ExtraArgument;
//     const markedSymbol = autoCouponMarked(orderNumber, couponCode);
//     persistenceHandles.autoAppliedCoupon.setItem(markedSymbol);
//     return res.data;
//   }
// );

export const refreshCartCommand = createAsyncThunk(
  'order/refreshCartCommand',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const rootState = getState() as RootState;
    const orderNumber = selectCurrentOrderNumber(rootState);
    if (!orderNumber) {
      return rejectWithValue('[refreshCartCommand]:order not found');
    }
    const res = await dispatch(refreshPrice.initiate(orderNumber));
    if ('error' in res) {
      return rejectWithValue(res.error);
    }
    return res.data;
  }
);

export const overWriteServiceItemPriceCommand = createAsyncThunk(
  'order/overWriteServiceItemPriceCommand',
  async (payload: { price: string; lineItemId: number }, { dispatch, getState, rejectWithValue }) => {
    const rootState = getState() as RootState;
    const orderNumber = selectCurrentOrderNumber(rootState);
    if (!orderNumber) {
      return rejectWithValue('[overWriteServiceItemPriceCommand]:order not found');
    }
    //price: number; lineItemId: number; number: string
    const res = await dispatch(
      overWriteServiceProductPrice.initiate({
        number: orderNumber,
        price: Number(payload.price),
        lineItemId: payload.lineItemId,
      })
    );
    if ('error' in res) {
      return rejectWithValue(res.error);
    }
    return res.data;
  }
);

export const getWebOrderByUidCommand = createAsyncThunk(
  'order/getCurrentOrderByUidCommand',
  async (
    {
      forceRefetch = false,
    }: {
      forceRefetch?: boolean;
    } = {},
    { dispatch, getState, rejectWithValue }
  ) => {
    if (!enableO2O) {
      return Promise.resolve();
    }
    const rootState = getState() as RootState;
    const uid = selectedCustomerId(rootState);
    if (!uid) {
      logger.error('getCurrentOrderByUidCommand failed: uid not found', { uid });
      return rejectWithValue('[getCurrentOrderByUidCommand]:uid not found');
    }
    const res = await dispatch(
      getWebOrderByUid.initiate(uid, {
        forceRefetch,
      })
    );
    if ('error' in res) {
      return rejectWithValue(res.error);
    }

    return res.data;
  }
);

export const changeWebOrderStatusCommand = createAsyncThunk(
  'order/changeWebOrderStatusCommand',
  async (_, { dispatch, extra, getState }) => {
    const loadingStatus = (getState() as RootState)?.order.loadingStatus;
    const webMergeOrderStatus = (getState() as RootState).order.webMergeOrderStatus;
    const { persistenceHandles } = extra as ExtraArgument;
    const accessToken = persistenceHandles.webAccessToken.getItem();
    const webOrderNumber = persistenceHandles.webOrderId.getItem() || undefined;
    const webOrderToken = persistenceHandles.webOrderToken.getItem();
    if (loadingStatus) return;
    let orderData = null;
    if (accessToken) {
      let data = null;
      try {
        const result = await dispatch(getWebOrderByCustomer.initiate()).unwrap();
        data = result;
        orderData = data;
      } catch (error) {
        console.log('🚀 ~ error:', error);
      }
      if (webOrderNumber && webOrderToken) {
        persistenceHandles.webOrderId.removeItem();
        persistenceHandles.webOrderToken.removeItem();
        if (data) {
          const userOrderId = data?.number;
          if (userOrderId !== webOrderNumber && !webMergeOrderStatus) {
            // 合并 order，成功或失败都更新 order id cookie
            try {
              const res = await dispatch(
                mergeOrder.initiate({
                  orderNumber: userOrderId,
                  orderId: webOrderNumber,
                  orderToken: webOrderToken,
                  skipError: false,
                })
              ).unwrap();
              orderData = res;
            } catch (error) {
              console.log('🚀 ~ error:', error);
            }
          }
        } else {
          // 创建新 web 订单
          // 合并 order，更新 order id cookie
          try {
            const res = await dispatch(createWebOrderForCurrentUser()).unwrap();
            const newOrderNumber = res?.number;
            if (!webMergeOrderStatus) {
              try {
                const res = await dispatch(
                  mergeOrder.initiate({
                    orderNumber: newOrderNumber,
                    orderId: webOrderNumber,
                    orderToken: webOrderToken,
                    skipError: false,
                  })
                ).unwrap();
                orderData = res;
              } catch (error) {
                console.log('🚀 ~ error:', error);
              }
            }
          } catch (error) {
            throw new Error(' ~ file: order.listener.ts , create web order failed');
          }
        }
      } else {
        if (data) {
          // 更新 order id cookie
          persistenceHandles.webOrderId.setItem(data.number);
        } else {
          // 删除现有 order id cookie
          // 创建新 web 订单
          persistenceHandles.webOrderId.removeItem();
          persistenceHandles.webOrderToken.removeItem();
          try {
            const res = await dispatch(createWebOrderForCurrentUser()).unwrap();
            orderData = res;
          } catch (error) {
            throw new Error(' ~ file: order.listener.ts , create web order failed');
          }
        }
      }
    } else {
      if (webOrderNumber && webOrderToken) {
        try {
          const data = await dispatch(
            getWebOrderByOrderNumber.initiate({
              orderNumber: webOrderNumber,
              isNeedSpreeOrderHeader: true,
            })
          ).unwrap();
          orderData = data;
          if (!data) {
            persistenceHandles.webOrderId.removeItem();
            persistenceHandles.webOrderToken.removeItem();
          }
        } catch (error) {
          persistenceHandles.webOrderId.removeItem();
          persistenceHandles.webOrderToken.removeItem();
        }
      } else {
        // 没有 access token、order id 、order token 创建新 web 订单
        // 更新 order id cookie
        try {
          const res = await dispatch(createWebOrderForCurrentUser()).unwrap();
          orderData = res;
        } catch (error) {
          throw new Error(' ~ file: order.listener.ts , create web order failed');
        }
      }
    }
    if (orderData) {
      dispatch(setOrder(orderData));
    }
  }
);
